import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { SubdomainService } from "src/app/core/_base/subdomain.service";
import { MadAuthService } from "src/app/core/mad-auth/mad-auth.service";
import { environment } from "src/environments/environment";

export type StoreToeReportTemplatePayload = {
  name: string,
  cover_page_included: boolean,
  toc_included: boolean,
  back_page_included: boolean,
  cover_page_title: string,
  client_id_included: boolean,
  assignment_id_included: boolean,
  toe_id_included: boolean,
  toe_id_label: string,
  date_of_instruction_included: boolean,
  footer_module_included: boolean,
  client_id: number,
  sections: Array<{
    name: string,
    included: boolean,
    chapters: Array<{
      name: string,
      included: boolean
    }>
  }>
}

type ToeReportTemplateListItemAPI = {
  id: number
  name: string
  terms_of_engagements_count: number
}

@Injectable({
  providedIn: 'root'
})
export class ToeReportTemplateService {
  constructor(
    private http: HttpClient,
    private authService: MadAuthService,
    private subdomainService: SubdomainService
  ) {}

  fetchList() {
    return this.http.get<{
      data: Array<ToeReportTemplateListItemAPI>,
      total: number,
      current_page: number,
      per_page: number
    }>(
      environment.baseApiUrl + `api/v2/${this.subdomainService.subDomain}/report-templates/toe`,
      {
        headers: this.authService.getAuthHeaders()
      }
    ).pipe()
  } 

  fetchTrashedList() {
    return this.http.get<{data: {
      id: number,
      name: string,
      deleted_by: string,
      deleted_at: string
    }[]}>(
      environment.baseApiUrl + `api/v2/${this.subdomainService.subDomain}/report-templates/toe/trashed`,
      {
        headers: this.authService.getAuthHeaders()
      }
    ).pipe(
      map(response => response.data)
    )
  }

  fetchAdminTrashedList() {
    return this.http.get<{data: {
      id: number,
      name: string,
      deleted_by: string,
      deleted_at: string
    }[]}>(
      environment.baseApiUrl + `api/v2/${this.subdomainService.subDomain}/report-templates/toe/admin-trashed`,
      {
        headers: this.authService.getAuthHeaders()
      }
    ).pipe(
      map(response => response.data)
    )
  }

  fetch(id: number) {
    return this.http.get<{
      data: {
        id: number,
        cover_page_included: number,
        toc_included: number,
        back_page_included: number,
        name: string,
        cover_page_title: string,
        client_id_included: number,
        assignment_id_included: number,
        toe_id_included: number,
        toe_id_label: string,
        date_of_instruction_included: number,
        footer_module_included: number,
        client_id: number,
        sections: Array<{
          id: number,
          included: number,
          name: string,
          default_name: string,
          chapters: Array<{
            id: number
            included: number
            name: string,
            default_name: string
          }>
        }>
      }
    }>(
      environment.baseApiUrl + `api/v2/${this.subdomainService.subDomain}/report-templates/toe/${id}`,
      {
        headers: this.authService.getAuthHeaders()
      }
    )
  }

  create(payload: StoreToeReportTemplatePayload) {
    return this.http.post<{
      data: {
        id: number,
        name: string
      }
    }>(
      environment.baseApiUrl + `api/v2/${this.subdomainService.subDomain}/report-templates/toe`,
      payload,
      {
        headers: this.authService.getAuthHeaders()
      }
    )
  }

  update(id: number, payload: StoreToeReportTemplatePayload) {
    return this.http.put<unknown>(
      environment.baseApiUrl + `api/v2/${this.subdomainService.subDomain}/report-templates/toe/${id}`,
      payload,
      {
        headers: this.authService.getAuthHeaders()
      }
    )
  }

  delete(id: number) {
    return this.http.delete<void>(
      environment.baseApiUrl + `api/v2/${this.subdomainService.subDomain}/report-templates/toe/${id}`,
      {
        headers: this.authService.getAuthHeaders()
      }
    )
  }

  deleteFromTrash(id: number) {
    return this.http.delete<void>(
      environment.baseApiUrl + `api/v2/${this.subdomainService.subDomain}/report-templates/toe/trashed/${id}`,
      {
        headers: this.authService.getAuthHeaders()
      }
    )
  }

  deleteFromAdminTrash(id: number) {
    return this.http.delete<void>(
      environment.baseApiUrl + `api/v2/${this.subdomainService.subDomain}/report-templates/toe/admin-trashed/${id}`,
      {
        headers: this.authService.getAuthHeaders()
      }
    )
  }

  flushTrash() {
    return this.http.delete<void>(
      environment.baseApiUrl + `api/v2/${this.subdomainService.subDomain}/report-templates/toe/flush`,
      {
        headers: this.authService.getAuthHeaders()
      }
    )
  }

  restoreFromTrash(id: number) {
    return this.http.post<void>(
      environment.baseApiUrl + `api/v2/${this.subdomainService.subDomain}/report-templates/toe/restore/${id}`,
      {},
      {
        headers: this.authService.getAuthHeaders()
      }
    )
  }

  restoreFromAdminTrash(id: number) {
    return this.http.post<void>(
      environment.baseApiUrl + `api/v2/${this.subdomainService.subDomain}/report-templates/toe/admin-restore/${id}`,
      {},
      {
        headers: this.authService.getAuthHeaders()
      }
    )
  }

  duplicate(id: number) {
    return this.http.post<void>(
      environment.baseApiUrl + `api/v2/${this.subdomainService.subDomain}/report-templates/toe/duplicate/${id}`,
      {},
      {
        headers: this.authService.getAuthHeaders()
      }
    )
  }
}